/**
 * Module deep links (见 20 模块深链与 URL 寻址).
 *
 * A deep link addresses **a module**, and the module id is written into the URL
 * rather than claimed by a manifest declaration. That is the whole security
 * design: ownership is structural, so two modules cannot contend for the same
 * address and an untrusted module cannot register itself as the target of one
 * that carries a credential (an OAuth `code`, say). It mirrors VS Code's
 * `vscode://<publisher>.<extension>/path`.
 *
 * The logical address is `<moduleId>/<rest>?<query>`; each runtime maps it into
 * its own namespace, and only the *recognition* differs:
 *
 * - web: `<origin>/<moduleId>/<rest>` — the app origin's root path space.
 * - desktop: `hibro://<moduleId>/<rest>` — the OS-registered scheme, which does
 *   nothing else (module assets live on the separate `hibro-assets:` scheme, so
 *   an OS-delivered URL can never also be a valid asset address).
 *
 * Both funnel into {@link parseModuleLink}, which owns the confinement policy.
 */

import { isUnsafeAssetSegment } from './asset-path';

/**
 * Prefix marking a path segment as **the platform's**, not a module's (见 20).
 *
 * The app origin's root is shared by the SPA, module assets and deep links, so
 * something has to separate them. A growing list of reserved words would work
 * but each addition breaks whatever module already uses that id; a prefix is a
 * *closed* rule — the platform can add `/_settings` or `/_sw.js` forever without
 * touching the module namespace. Same solution Next.js (`_next`), Nuxt
 * (`_nuxt`) and Astro (`_astro`) reached for the identical problem.
 */
export const PLATFORM_PATH_PREFIX = '_';

/**
 * Whether `id` is reserved for the platform (见 20). Enforced by
 * `validateManifest`, so a module can never occupy a system address.
 */
export function isReservedModuleId(id: string): boolean {
  return id.startsWith(PLATFORM_PATH_PREFIX);
}

/**
 * Whether a request path addresses the **platform's file space** (`/_…`) rather
 * than a module deep link (见 20). The fork every origin router needs:
 *
 * - a platform path names a *file* (`/_mod/<id>/…`, `/_sw.js`, `/_product.json`,
 *   `/_assets/…`), so a miss is a 404;
 * - the unmarked root space carries deep links (`/im/chat/1`), which name no
 *   file and must boot the workbench shell.
 *
 * Answering a missed platform path with the shell instead is what makes a failed
 * `@storage` download arrive as a `200 text/html` named `.zip` — a corrupt file
 * where the caller expected a visible failure. Hence one predicate, applied by
 * both the dev server and the deployed origin.
 *
 * `/` is not a platform path: it is the shell's own address.
 */
export function isPlatformPath(pathname: string): boolean {
  const [first] = pathname
    .split('/')
    .filter((segment) => segment !== '' && segment !== '.');
  return first !== undefined && isReservedModuleId(first);
}

/**
 * Asset-scheme host serving the shared runtime singleton shims (见 12):
 * `hibro-assets://_runtime/<name>`.
 *
 * Lives here rather than in `@hibro/platform-main` because it occupies the same
 * authority slot module ids do, so it is governed by the reservation rule above
 * — and because the build tooling needs it too (it emits the renderer import
 * map). It used to be declared twice, in the main process and again as a
 * hand-copied mirror in the codegen.
 */
export const RUNTIME_HOST = `${PLATFORM_PATH_PREFIX}runtime`;

/**
 * Identity the app shell states when it calls a per-module renderer API on its
 * own behalf (见 20) — today only the navigation guard's `openLink`, which routes
 * an address no module asked it to route.
 *
 * `openLink` attributes its caller for the log, and "the workbench itself" is a
 * truthful answer where borrowing a module's id would not be. Reserved by
 * {@link isReservedModuleId}, so it can never collide with a real module.
 */
export const WORKBENCH_CALLER_ID = `${PLATFORM_PATH_PREFIX}workbench`;

/**
 * Activation event a module declares to opt into deep links (见 20), the
 * counterpart of VS Code's `onUri`.
 *
 * Declaring it derives a per-module `onUrl:<moduleId>` internally, which is what
 * the host fires — so delivery activates exactly the addressed module, and a
 * module that never opted in is never woken by a URL at all.
 */
export const URL_ACTIVATION_EVENT = 'onUrl';

/** The derived, per-module activation event a deep link fires (见 20). */
export function urlActivationEvent(moduleId: string): string {
  return `${URL_ACTIVATION_EVENT}:${moduleId}`;
}

/**
 * How a link reached its owning module (见 20).
 *
 * The two differ in **who decided the owner**, which is why they fail in
 * opposite directions:
 *
 * - `deep-link`: the module id is written into the URL, so the address is the
 *   decision. Such a link may carry a one-time credential, hence it is never
 *   handed anywhere else — a module that declines it fails closed.
 * - `app-link`: an ordinary http(s) address the platform matched against
 *   `contributes.urlOwnership`. The platform guessed, so a decline is a normal
 *   answer and the link falls back to the built-in browser.
 */
export type ModuleLinkSource = 'deep-link' | 'app-link';

/** A link resolved to its target module and payload. */
export interface ModuleLink {
  /** The addressed module. Never platform-reserved (those are rejected here). */
  moduleId: string;
  /**
   * Path within the module, always leading-slashed (`/login`). `/` when the link
   * addressed the module itself with no further path. For an `app-link` this is
   * the URL's own pathname — the module owns the whole address there, so no
   * segment is consumed as its id.
   */
  path: string;
  /** Query parameters, flattened last-wins. Empty object when there are none. */
  query: Record<string, string>;
  /** How the owner was decided; see {@link ModuleLinkSource}. */
  source: ModuleLinkSource;
  /**
   * The original URL, present for `app-link` — an external address whose host is
   * part of the payload, which `path`/`query` alone cannot express. Absent for a
   * deep link, where the address is nothing but module id + path.
   */
  url?: string;
}

/**
 * A module's link handler (见 20). Registered via `context.registerUrlHandler`;
 * awaited by the host, so a rejection surfaces rather than becoming an unhandled
 * rejection.
 *
 * Returning `false` **declines** the link: the module was addressed but will not
 * present it, so the platform falls back to the built-in browser. Only
 * meaningful for an `app-link` (see {@link ModuleLinkSource}). Two rules follow
 * from the fallback and belong to whoever writes a handler:
 *
 * - **Decide before presenting.** Navigating and then declining shows the user
 *   both the module view and a browser page.
 * - **Decide locally.** The host waits only briefly; a handler that awaits the
 *   network risks timing out into the browser, where the user lands on a login
 *   page instead of the document.
 */
export type ModuleUrlHandler = (
  link: ModuleLink,
) => void | boolean | Promise<void | boolean>;

/**
 * A deployment-owned rewrite from one logical deep-link path prefix to another
 * (见 20 公开入口别名).
 *
 * Both prefixes are leading- and trailing-slashed. Rewriting happens once,
 * before structural module resolution, so the target still has to be a normal
 * `<moduleId>/<rest>` address and modules cannot claim aliases themselves.
 */
export interface ModuleLinkAlias {
  readonly from: string;
  readonly to: string;
}

export interface ResolvedModuleLinkAliases {
  readonly aliases: readonly ModuleLinkAlias[];
  readonly problems: readonly string[];
}

/**
 * Validate the `platform.linkAliases` product value.
 *
 * Invalid entries are rejected individually and reported to the caller for the
 * unified log pipeline. Prefixes deliberately require a trailing slash: aliases
 * address a subtree and can never capture a similarly named sibling.
 */
export function resolveModuleLinkAliases(
  value: unknown,
): ResolvedModuleLinkAliases {
  if (value === undefined) {
    return { aliases: [], problems: [] };
  }
  if (!Array.isArray(value)) {
    return {
      aliases: [],
      problems: ['platform.linkAliases must be an array'],
    };
  }

  const aliases: ModuleLinkAlias[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();
  value.forEach((entry, index) => {
    const input = entry as { from?: unknown; to?: unknown } | null;
    const from = input?.from;
    const to = input?.to;
    if (
      typeof from !== 'string' ||
      typeof to !== 'string' ||
      !isValidModuleLinkAliasPrefix(from) ||
      !isValidModuleLinkAliasPrefix(to)
    ) {
      problems.push(
        `platform.linkAliases[${String(index)}] must contain canonical from/to path prefixes`,
      );
      return;
    }
    if (seen.has(from)) {
      problems.push(
        `platform.linkAliases[${String(index)}] duplicates prefix "${from}"`,
      );
      return;
    }
    seen.add(from);
    aliases.push({ from, to });
  });

  // More specific public prefixes win without making declaration order
  // semantically significant.
  aliases.sort((left, right) => right.from.length - left.from.length);
  return { aliases, problems };
}

function isValidModuleLinkAliasPrefix(prefix: string): boolean {
  if (
    prefix === '/' ||
    !prefix.startsWith('/') ||
    !prefix.endsWith('/') ||
    prefix.includes('?') ||
    prefix.includes('#')
  ) {
    return false;
  }
  const parsed = parseModuleLinkParts(prefix);
  if (!parsed) {
    return false;
  }
  const canonical = `/${parsed.moduleId}${parsed.path}`;
  return `${canonical.replace(/\/+$/, '')}/` === prefix;
}

function applyModuleLinkAlias(
  pathname: string,
  aliases: readonly ModuleLinkAlias[],
): string {
  const logicalPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const alias = aliases.find(({ from }) => logicalPath.startsWith(from));
  return alias
    ? `${alias.to}${logicalPath.slice(alias.from.length)}`
    : logicalPath;
}

/**
 * Parse the module-addressing part of a deep link: `<moduleId>/<rest>?<query>`.
 *
 * Takes the already-split pathname and search so both runtimes can reuse it —
 * the web shell has an `<origin>`-relative path, the desktop gets a `hibro://`
 * URL whose authority is the module id. Fail closed (`undefined`) on an empty
 * path, a platform-reserved first segment, or any segment that could climb out
 * (the same segment policy module assets use, {@link isUnsafeAssetSegment}).
 */
export function parseModuleLinkParts(
  pathname: string,
  search = '',
): ModuleLink | undefined {
  const segments = pathname
    .split('/')
    .filter((segment) => segment !== '' && segment !== '.');
  if (segments.length === 0 || segments.some(isUnsafeAssetSegment)) {
    return undefined;
  }
  const [moduleId, ...rest] = segments;
  if (isReservedModuleId(moduleId)) {
    return undefined;
  }
  return {
    moduleId,
    path: `/${rest.join('/')}`,
    query: flattenQuery(search),
    source: 'deep-link',
  };
}

/** Query string → last-wins flat map. Empty object when there is none. */
function flattenQuery(search: string): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    query[key] = value;
  }
  return query;
}

/**
 * Parse a full deep-link URL (either runtime's shape) into its target module.
 *
 * Returns `undefined` for anything that is not a deep link — including an
 * ordinary page URL on web, which is why the caller may hand every launch URL
 * here without pre-filtering.
 */
export function parseModuleLink(
  url: string,
  opts: { scheme: string; aliases?: readonly ModuleLinkAlias[] },
): ModuleLink | undefined {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  // Desktop: the OS-registered scheme puts the module id in the authority.
  // `new URL('hibro://account/login')` yields host `account`, pathname `/login`.
  if (parsed.protocol === `${opts.scheme}:`) {
    return parseModuleLinkParts(
      applyModuleLinkAlias(
        `${parsed.hostname}${parsed.pathname}`,
        opts.aliases ?? [],
      ),
      parsed.search,
    );
  }
  // Web: an http(s) page URL whose root path space carries the address.
  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    return parseModuleLinkParts(
      applyModuleLinkAlias(parsed.pathname, opts.aliases ?? []),
      parsed.search,
    );
  }
  return undefined;
}

/**
 * One module's claim over an external address space (见 20 外链归属路由), from
 * `contributes.urlOwnership`.
 *
 * Ownership of an **external** http(s) address cannot be structural the way a
 * deep link's is — the address belongs to the module's own backend, not to the
 * platform's namespace — so here it *is* declared. What keeps that from becoming
 * the squattable route table `20` rejected: a declaration is an origin plus a
 * path prefix with no wildcards ({@link isValidUrlOwnershipPrefix}), two modules
 * claiming the same address cancel each other out
 * ({@link matchUrlOwner}). Which prefixes a published package may declare is a
 * store-review decision; the client honours the declaration either way.
 */
export interface UrlOwnershipEntry {
  moduleId: string;
  /** Declared `<origin>/<path prefix>`, already validated. */
  prefix: string;
}

/**
 * Whether `prefix` may be declared in `contributes.urlOwnership` (见 20).
 *
 * Deliberately narrow, because this *claims user clicks*: a wildcard origin
 * would let one module intercept every link in the app. Hence: exact http(s)
 * origin, a path beyond the root, no wildcards, no query/hash.
 */
export function isValidUrlOwnershipPrefix(prefix: string): boolean {
  if (typeof prefix !== 'string' || prefix.includes('*')) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(prefix);
  } catch {
    return false;
  }
  return (
    (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
    parsed.hostname.length > 0 &&
    parsed.pathname.length > 1 &&
    parsed.search === '' &&
    parsed.hash === '' &&
    // `new URL` normalizes (lowercases the origin, resolves `..`); requiring the
    // declaration to already be in that form keeps "what was declared" and "what
    // is matched" the same string.
    prefix === `${parsed.origin}${parsed.pathname}`
  );
}

/** Outcome of matching a URL against the aggregated ownership declarations. */
export type UrlOwnerMatch =
  | { readonly kind: 'owner'; readonly moduleId: string }
  /** Two or more modules claim this address; fail closed (见 20). */
  | { readonly kind: 'conflict'; readonly moduleIds: readonly string[] };

/**
 * The module owning `url`, or a conflict when several claim it (见 20).
 *
 * Matching is `<origin><pathname>` against each declared prefix, at a path
 * boundary — `https://x.io/doc` must not capture `https://x.io/document/1`.
 * Query and hash never participate: the same document with different query is
 * still that module's address.
 *
 * A conflict is not resolved by specificity on purpose. Picking the longest
 * prefix would hand the address to whichever module declared more of it, which
 * is exactly the squatting move the declaration model has to make pointless.
 */
export function matchUrlOwner(
  url: string,
  entries: readonly UrlOwnershipEntry[],
): UrlOwnerMatch | undefined {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return undefined;
  }
  const target = `${parsed.origin}${parsed.pathname}`;
  const owners = new Set<string>();
  for (const entry of entries) {
    if (matchesPrefix(target, entry.prefix)) {
      owners.add(entry.moduleId);
    }
  }
  if (owners.size === 0) {
    return undefined;
  }
  const moduleIds = [...owners];
  return moduleIds.length === 1
    ? { kind: 'owner', moduleId: moduleIds[0] }
    : { kind: 'conflict', moduleIds };
}

function matchesPrefix(target: string, prefix: string): boolean {
  if (!target.startsWith(prefix)) {
    return false;
  }
  const rest = target.slice(prefix.length);
  return rest === '' || prefix.endsWith('/') || rest.startsWith('/');
}

/**
 * The {@link ModuleLink} handed to `moduleId` for an owned external address
 * (见 20). Keeps the whole URL (its host is part of the payload) alongside the
 * path/query split every handler already reads.
 */
export function appLink(moduleId: string, url: string): ModuleLink {
  const parsed = new URL(url);
  return {
    moduleId,
    path: parsed.pathname,
    query: flattenQuery(parsed.search),
    source: 'app-link',
    url,
  };
}
